import React, { useState } from 'react'
import { Editor as IEditor } from '@tiptap/react'
import { makeStyles } from '@material-ui/core/styles'
import { Button, ButtonGroup } from '@siftscience/focus-components/button'

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    padding: '8px',
    gap: '8px',
    width: '100%',
    borderBottom: '1px solid #E0E0E0'
  }
}))

interface EditorToolbarProps {
  editor: IEditor
}

export const EditorToolbar = ({ editor }: EditorToolbarProps) => {
  const [tableOptionsVisible, setTableOptionsVisible] = useState(false)
  const classes = useStyles()
  if (!editor) {
    return null
  }

  return (
    <div className={classes.wrapper}>
      <ButtonGroup>
        <Button
          lined
          variant={editor.isActive('paragraph') ? 'primary' : 'secondary'}
          onClick={() =>
            editor.chain().focus().setParagraph().unsetColor().run()
          }
        >
          Paragraph
        </Button>
        <Button
          lined
          variant={
            editor.isActive('heading', { level: 1 }) ? 'primary' : 'secondary'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 1 })
              .setColor('rgb(42, 76, 136)')
              .run()
          }
        >
          H1
        </Button>
        <Button
          lined
          variant={
            editor.isActive('heading', { level: 2 }) ? 'primary' : 'secondary'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 2 })
              .setColor('rgb(42, 76, 136)')
              .run()
          }
        >
          H2
        </Button>
        <Button
          lined
          variant={
            editor.isActive('heading', { level: 3 }) ? 'primary' : 'secondary'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 3 })
              .setColor('rgb(42, 76, 136)')
              .run()
          }
        >
          H3
        </Button>
        <Button
          lined
          variant={
            editor.isActive('heading', { level: 4 }) ? 'primary' : 'secondary'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 4 })
              .setColor('rgb(42, 76, 136)')
              .run()
          }
        >
          H4
        </Button>
        <Button
          lined
          variant={
            editor.isActive('heading', { level: 5 }) ? 'primary' : 'secondary'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 5 })
              .setColor('rgb(42, 76, 136)')
              .run()
          }
        >
          H5
        </Button>
        <Button
          lined
          variant={
            editor.isActive('heading', { level: 6 }) ? 'primary' : 'secondary'
          }
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleHeading({ level: 6 })
              .setColor('rgb(42, 76, 136)')
              .run()
          }
        >
          H6
        </Button>
      </ButtonGroup>
      <ButtonGroup>
        <Button
          lined
          variant={editor.isActive('bulletList') ? 'primary' : 'secondary'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          Bullet list
        </Button>
        <Button
          lined
          variant={editor.isActive('orderedList') ? 'primary' : 'secondary'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          Ordered list
        </Button>
      </ButtonGroup>
      <ButtonGroup>
        <Button
          lined
          variant={editor.isActive('bold') ? 'primary' : 'secondary'}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          Bold
        </Button>
        <Button
          lined
          variant={editor.isActive('italic') ? 'primary' : 'secondary'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          Italic
        </Button>
        <Button
          lined
          variant={editor.isActive('underline') ? 'primary' : 'secondary'}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          Underline
        </Button>
      </ButtonGroup>
      <ButtonGroup>
        <Button
          lined
          variant={editor.isActive('blockquote') ? 'primary' : 'secondary'}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          Blockquote
        </Button>
      </ButtonGroup>
      <ButtonGroup>
        <Button
          lined
          variant={'secondary'}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          Horizontal line
        </Button>
      </ButtonGroup>

      <ButtonGroup>
        <Button
          variant="secondary"
          lined
          onClick={() => setTableOptionsVisible(!tableOptionsVisible)}
        >
          {tableOptionsVisible ? 'Hide' : 'Show'} Table options
        </Button>
      </ButtonGroup>
      {tableOptionsVisible && (
        <>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
            >
              Insert table
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().deleteTable().run()}
              disabled={!editor.can().deleteTable()}
            >
              Delete table
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              disabled={!editor.can().addColumnBefore()}
            >
              Add column before
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              disabled={!editor.can().addColumnAfter()}
            >
              Add column after
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              disabled={!editor.can().deleteColumn()}
            >
              Delete column
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().addRowBefore().run()}
              disabled={!editor.can().addRowBefore()}
            >
              Add row before
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              disabled={!editor.can().addRowAfter()}
            >
              Add row after
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().deleteRow().run()}
              disabled={!editor.can().deleteRow()}
            >
              Delete row
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().mergeCells().run()}
              disabled={!editor.can().mergeCells()}
            >
              Merge cells
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().splitCell().run()}
              disabled={!editor.can().splitCell()}
            >
              Split cell
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().mergeOrSplit().run()}
              disabled={!editor.can().mergeOrSplit()}
            >
              Merge or split
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
              disabled={!editor.can().toggleHeaderColumn()}
            >
              Toggle header column
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              disabled={!editor.can().toggleHeaderRow()}
            >
              Toggle header row
            </Button>
            <Button
              variant="secondary"
              onClick={() => editor.chain().focus().toggleHeaderCell().run()}
              disabled={!editor.can().toggleHeaderCell()}
            >
              Toggle header cell
            </Button>
          </ButtonGroup>
        </>
      )}
    </div>
  )
}
